import React, { useEffect, useState } from 'react';
import Modal from '../../../components/Modal';
import { tabGroupStyle, tabBtn } from './styles';

const inputClass =
  'w-full h-9 rounded-lg border border-[var(--border)] bg-[var(--bg-card)] px-3 text-[13px] text-[var(--text)] outline-none focus:border-indigo-400';

const secondaryBtn =
  'h-9 rounded-lg border border-[var(--border)] px-3 text-[12px] font-bold text-[var(--text)] hover:bg-[var(--bg-subtle)] disabled:opacity-50';

const primaryBtn =
  'h-9 rounded-lg bg-indigo-600 px-3 text-[12px] font-bold text-white hover:bg-indigo-700 disabled:opacity-50';

const dangerBtn =
  'h-9 rounded-lg border border-red-200 px-3 text-[12px] font-bold text-red-600 hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-950/30 disabled:opacity-50';

// [4대보험 | 3.3%] 탭(2026-11-03 합치기-2) — 3.3% 부서 목록은 4대보험 회사 부서와 따로 추가·이름 바꾸기·삭제한다.
//   freelancerSettings 를 넘기면 탭이 생긴다. 부르는 쪽이 고칠 수 없는 목록은 null 로 넘겨 그 탭을 뺀다.
const TAB_NOTE = {
  insured: '4대보험 직원이 고르는 부서입니다. 이 회사만 씁니다.',
  freelance: '3.3% 인원이 고르는 부서입니다. 이 테넌트의 모든 회사가 같은 목록을 씁니다.',
};

export default function DepartmentSettingsModal({
  open,
  onClose,
  title = '부서 설정',
  settings,
  freelancerSettings = null,
}) {
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [tabKey, setTabKey] = useState('');
  const tabs = [
    settings && { key: 'insured', label: '4대보험', settings },
    freelancerSettings && { key: 'freelance', label: '3.3%', settings: freelancerSettings },
  ].filter(Boolean);
  const active = tabs.find((t) => t.key === tabKey) || tabs[0];
  const current = active?.settings;
  const showTabs = Boolean(freelancerSettings);

  useEffect(() => {
    if (!open) {
      setNewName('');
      setEditingId(null);
      setEditingName('');
      setTabKey('');
    }
  }, [open]);

  function switchTab(key) {
    setTabKey(key);
    setNewName('');
    setEditingId(null);
    setEditingName('');
  }

  async function handleAdd() {
    const ok = await current.addDepartment(newName);
    if (ok) setNewName('');
  }

  async function handleSaveEdit(id) {
    const ok = await current.renameDepartment(id, editingName);
    if (ok) {
      setEditingId(null);
      setEditingName('');
    }
  }

  function startEdit(dept) {
    setEditingId(dept.id);
    setEditingName(dept.name || '');
  }

  if (!current) return null;
  return (
    <Modal open={open} title={title} icon="🏢" onClose={onClose} width="min(640px, calc(100vw - 48px))">
      <div className="grid gap-4">
        {showTabs && (
          <div className="grid gap-2">
            <div style={tabGroupStyle} role="group" aria-label="부서 구분">
              {tabs.map((t) => (
                <button key={t.key} type="button" style={tabBtn(active.key === t.key)} onClick={() => switchTab(t.key)}>{t.label}</button>
              ))}
            </div>
            <div className="text-[12px] text-[var(--text-muted)]">{TAB_NOTE[active.key]}</div>
          </div>
        )}
        <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-subtle)] p-3">
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <input
              className={inputClass}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="새 부서명"
              disabled={current.saving}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
              }}
            />
            <button className={primaryBtn} onClick={handleAdd} disabled={current.saving}>
              추가
            </button>
          </div>
        </div>

        <div className="grid gap-2">
          {current.loading && (
            <div className="py-8 text-center text-[13px] text-[var(--text-muted)]">
              부서 목록을 불러오는 중입니다.
            </div>
          )}

          {!current.loading && current.departments.length === 0 && (
            <div className="py-8 text-center text-[13px] text-[var(--text-muted)]">
              등록된 부서가 없습니다.
            </div>
          )}

          {!current.loading && current.departments.map((dept) => {
            const isEditing = editingId === dept.id;
            return (
              <div
                key={dept.id}
                className="grid grid-cols-[1fr_auto] items-center gap-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] px-3 py-2"
              >
                {isEditing ? (
                  <input
                    className={inputClass}
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    disabled={current.saving}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit(dept.id);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    autoFocus
                  />
                ) : (
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-bold text-[var(--text)]">
                      {dept.name}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-1.5">
                  {isEditing ? (
                    <>
                      <button
                        className={primaryBtn}
                        onClick={() => handleSaveEdit(dept.id)}
                        disabled={current.saving}
                      >
                        저장
                      </button>
                      <button
                        className={secondaryBtn}
                        onClick={() => setEditingId(null)}
                        disabled={current.saving}
                      >
                        취소
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className={secondaryBtn}
                        onClick={() => startEdit(dept)}
                        disabled={current.saving}
                      >
                        수정
                      </button>
                      <button
                        className={dangerBtn}
                        onClick={() => current.deleteDepartment(dept.id)}
                        disabled={current.saving}
                      >
                        삭제
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
